/**
 * SearchableSelect — typeahead-friendly Combobox.
 *
 * IMPORTANT (v36 urgent fix May 20, 2026): This component is intentionally implemented WITHOUT
 * Radix Popover. Radix Popover renders its content in a Portal which becomes `aria-hidden` when
 * placed inside a Radix Dialog with `modal=true` (default), making the popover trigger clicks
 * land but the dropdown un-interactable. Rather than fight portal/focus-trap ordering, we render
 * the dropdown inline as an absolutely-positioned panel anchored to the trigger. This works
 * reliably inside Dialog, Sheet, and any other modal wrapper.
 *
 * Drop-in replacement for shadcn Select for long lists (e.g., Departments) where the standard
 * Radix select bottoms out and is not searchable.
 *
 * Usage:
 *   <SearchableSelect
 *     value={value}
 *     onChange={setValue}
 *     options={[{ value: 'Cardiology', label: 'Cardiology' }, …]}
 *     placeholder="Select department"
 *     searchPlaceholder="Search departments…"
 *     data-testid="department-select"
 *   />
 */
import { useState, useRef, useEffect, useMemo } from 'react';
import { Check, ChevronsUpDown, Search, X } from 'lucide-react';
import { cn } from '../../lib/utils';

export function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyText = 'No matches.',
  className = '',
  disabled = false,
  ...rest
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find(o => o.value === value);

  // Close on click-outside
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Auto-focus the search input when opening
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 0);
    }
  }, [open]);

  const filtered = useMemo(() => {
    // Dedupe options by value first (defensive — caller may pass duplicates from concatenated
    // API + fallback lists) and filter only against label (filtering against value caused
    // unrelated matches when a value field happened to contain the typed substring).
    const seen = new Set();
    const deduped = options.filter(o => {
      if (seen.has(o.value)) return false;
      seen.add(o.value);
      return true;
    });
    const q = query.trim().toLowerCase();
    if (!q) return deduped;
    return deduped.filter(o => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const testid = rest['data-testid'] || 'searchable';

  return (
    <div ref={wrapperRef} className={cn('relative w-full', className)}>
      {/* Trigger */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={testid}
        className={cn(
          'w-full inline-flex items-center justify-between gap-2 h-10 px-3 py-2 text-sm rounded-md border border-input bg-background',
          'hover:bg-accent/30 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
          disabled && 'opacity-50 cursor-not-allowed',
          !value && 'text-slate-400',
        )}>
        <span className="truncate text-left">{selected ? selected.label : placeholder}</span>
        <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
      </button>

      {/* Dropdown panel (inline, NOT portaled — works inside Dialog without aria-hidden trap) */}
      {open && (
        <div
          className="absolute left-0 right-0 top-full mt-1 z-[80] rounded-md border border-slate-200 dark:border-slate-700 bg-popover dark:bg-slate-900 text-popover-foreground shadow-lg overflow-hidden"
          data-testid={`${testid}-panel`}>
          {/* Search */}
          <div className="flex items-center gap-2 px-2.5 py-2 border-b border-slate-100 dark:border-slate-800">
            <Search className="h-3.5 w-3.5 text-slate-400 shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400"
              data-testid={`${testid}-input`}
              onKeyDown={(e) => {
                if (e.key === 'Escape') { setOpen(false); setQuery(''); }
                if (e.key === 'Enter' && filtered.length === 1) {
                  onChange(filtered[0].value);
                  setOpen(false);
                  setQuery('');
                }
              }}
            />
            {query && (
              <button type="button" onClick={() => setQuery('')} className="text-slate-400 hover:text-slate-700" aria-label="Clear search">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Options */}
          <div className="max-h-64 overflow-y-auto py-1" role="listbox">
            {filtered.length === 0 ? (
              <p className="px-3 py-6 text-center text-xs text-slate-400">{emptyText}</p>
            ) : (
              filtered.map(opt => {
                const isSelected = opt.value === value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => { onChange(opt.value); setOpen(false); setQuery(''); }}
                    className={cn(
                      'w-full text-left flex items-center gap-2 px-3 py-2 text-sm',
                      'hover:bg-slate-100 dark:hover:bg-slate-800 focus:bg-slate-100 dark:focus:bg-slate-800 focus:outline-none',
                      isSelected && 'bg-emerald-50 dark:bg-emerald-900/20 font-medium text-emerald-900 dark:text-emerald-200'
                    )}
                    data-testid={`${testid}-option-${opt.value}`}>
                    <Check className={cn('h-3.5 w-3.5 shrink-0', isSelected ? 'opacity-100 text-emerald-600' : 'opacity-0')} />
                    <span className="truncate">{opt.label}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
